// Persistent basemap tile cache — registration for public/tile-cache-sw.js.
//
// WHY THIS EXISTS (measured 2026-09-02, probed not assumed):
//     CARTO     cache-control: max-age=15552000   ->  180 days
//     MapTiler  cache-control: max-age=28800      ->    8 hours
// The MAP-01 provider swap inherited MapTiler's 8-hour header. Tiles that used to come
// off the browser's disk cache for six months now go stale overnight, so the first look
// at any map each day re-fetches the whole viewport from the network. A single-session
// benchmark cannot see this, which is why the swap shipped stating "raster-to-raster
// changes neither" — that was true of CARTO's header, not of MapTiler's. Cold latency
// compounds it: a 20-tile retina viewport measured 1.34 s on MapTiler vs 0.99 s on CARTO.
//
// VENDOR-INDEPENDENT ON PURPOSE. The fastest fix today would be to go back to CARTO, but
// that returns us to a raster product CARTO is retiring — the very thing MAP-01 exists to
// escape (and CARTO with a wrong key is byte-identical to CARTO with no key, so it would
// also put the "API KEY REQUIRED" watermark back until a CARTO key is obtained). Caching
// locally makes the provider's own header irrelevant, so this holds for whoever we are on
// next.
//
// It also blunts the z21/z22 cost of the provider swap as a side-effect: MapTiler's
// maxNativeZoom of 22 means zooming to cellar scale fetches two extra viewports of real
// tiles where CARTO upscaled cached z20 imagery for free. Cached, that is paid once.
//
// ONE SOURCE OF TRUTH FOR THE HOST ALLOWLIST: BASEMAP_PROVIDERS. The hosts are passed to
// the worker in its script URL instead of being duplicated inside it, so adding a provider
// cannot silently leave its tiles uncached.

import { BASEMAP_PROVIDERS } from "../components/maps/basemap";
import type { BasemapProvider } from "../components/maps/basemap";

/** Leaflet's own default when a template contains `{s}` and no `subdomains` option is
 *  given (SpatialMap gives none), so these are the hostnames CARTO tiles actually come
 *  from. Miss them and two-thirds of a CARTO viewport would bypass the cache. */
const LEAFLET_DEFAULT_SUBDOMAINS = ["a", "b", "c"] as const;

/** Hostnames a single provider's tiles can arrive from.
 *
 *  Returns [] rather than throwing on an unparseable template: this runs during app boot,
 *  and a typo'd template must degrade to "not cached", never to a blank app. */
export function tileHostsFor(urlTemplate: string): string[] {
  const expanded = urlTemplate.includes("{s}")
    ? LEAFLET_DEFAULT_SUBDOMAINS.map((s) => urlTemplate.replace("{s}", s))
    : [urlTemplate];
  const hosts: string[] = [];
  for (const template of expanded) {
    try {
      // Remaining Leaflet placeholders ({z}/{x}/{y}/{r}/{key}) only need to become
      // SOMETHING parseable — we read the hostname and discard the rest.
      hosts.push(new URL(template.replace(/\{[^}]*\}/g, "x")).hostname);
    } catch {
      // skip this template
    }
  }
  return hosts;
}

/** Every host any configured provider can serve tiles from, deduped and ordered so the
 *  worker URL is stable across loads (an unstable URL would re-register every time). */
export function tileHosts(
  providers: Record<string, BasemapProvider> = BASEMAP_PROVIDERS,
): string[] {
  return [
    ...new Set(
      Object.values(providers).flatMap((p) => tileHostsFor(p.urlTemplate)),
    ),
  ].sort();
}

/** The worker's script URL, carrying its configuration. `v` is bumped only when the
 *  worker's own logic changes; the host list changing is enough to update it on its own. */
export function tileCacheWorkerUrl(hosts: string[] = tileHosts()): string {
  return `/tile-cache-sw.js?v=1&hosts=${encodeURIComponent(hosts.join(","))}`;
}

/** Register the tile cache. Safe to call unconditionally.
 *
 *  NEVER throws and never blocks boot — same posture as basemap.ts's "a visibly wrong map
 *  is survivable, a hard failure is not". If registration is unsupported or fails, the
 *  worst case is the status quo ante: tiles come from the network on the provider's own
 *  schedule. Deferred to `load` so it does not contend with the initial page load. */
export function registerTileCache(): void {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    return;
  }
  const start = (): void => {
    navigator.serviceWorker
      .register(tileCacheWorkerUrl(), { scope: "/" })
      .catch(() => {
        /* unsupported, blocked by policy, or served over plain http — not fatal */
      });
  };
  if (document.readyState === "complete") {
    start();
  } else {
    window.addEventListener("load", start, { once: true });
  }
}
